import { useState } from "react";
import { Card, BtnPrimary, BtnGhost, Input } from "../components/ui";
import { S } from "../theme";

function Inventory({ protocol, inventory, setInventory }) {
  const [adding, setAdding] = useState(null); // peptide_id being added/edited
  const [vials, setVials] = useState("");
  const [dosesPerVial, setDosesPerVial] = useState("");
  const [threshold, setThreshold] = useState("5");
  const [notes, setNotes] = useState("");

  // Merge protocol with inventory data
  const items = protocol.map(p => {
    const inv = inventory.find(i => i.peptide_id === p.peptide_id);
    const vialsOnHand = inv ? inv.vials_on_hand : 0;
    const dpv = inv ? inv.doses_per_vial : 1;
    const dosesUsed = inv ? inv.doses_used : 0;
    const totalDoses = vialsOnHand * dpv;
    const remaining = Math.max(0, totalDoses - dosesUsed);
    const reorderAt = inv ? inv.reorder_threshold : 5;
    const isLow = remaining <= reorderAt && remaining > 0;
    const isEmpty = remaining === 0;
    const pct = totalDoses > 0 ? (remaining / totalDoses) * 100 : 0;
    return { ...p, inv, vialsOnHand, dpv, dosesUsed, totalDoses, remaining, reorderAt, isLow, isEmpty, pct };
  });

  const lowCount = items.filter(i => i.isLow || i.isEmpty).length;

  function openEdit(item) {
    setAdding(item.peptide_id);
    setVials(item.vialsOnHand.toString());
    setDosesPerVial(item.dpv.toString());
    setThreshold(item.reorderAt.toString());
    setNotes(item.inv?.notes || "");
  }

  function openAdd(p) {
    setAdding(p.peptide_id);
    setVials("");
    setDosesPerVial("");
    setThreshold("5");
    setNotes("");
  }

  function handleSave() {
    if (!adding) return;
    const p = protocol.find(x => x.peptide_id === adding);
    if (!p) return;
    const existing = inventory.find(i => i.peptide_id === adding);
    const entry = {
      ...(existing || {}),
      peptide_id: p.peptide_id,
      peptide_name: p.name,
      vials_on_hand: Math.max(0, parseInt(vials) || 0),
      doses_per_vial: Math.max(1, parseInt(dosesPerVial) || 1),
      doses_used: existing ? existing.doses_used : 0,
      reorder_threshold: Math.max(0, parseInt(threshold) || 5),
      notes: notes.trim() || null,
    };
    setInventory(entry);
    setAdding(null);
  }

  function handleRestock(item) {
    if (!item.inv) return;
    const entry = {
      ...item.inv,
      doses_used: 0,
    };
    setInventory(entry);
  }

  const labelStyle = { fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: S.muted, marginBottom: 4 };

  return (
    <div style={{ maxWidth: 800, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: "1.5rem", color: S.ink, margin: 0 }}>
          Inventory
        </h1>
        <p style={{ color: S.muted, fontSize: "0.82rem", marginTop: 6 }}>
          Track your peptide supply — vials on hand, doses remaining, and reorder alerts.
        </p>
      </div>

      {/* Summary bar */}
      {protocol.length > 0 && (
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          <div style={{ background: S.white, border: `1px solid ${S.border}`, borderRadius: 14, padding: "14px 20px", flex: 1, minWidth: 140 }}>
            <div style={labelStyle}>Tracked</div>
            <div style={{ fontSize: "1.3rem", fontWeight: 800, color: S.ink, fontFamily: "'Fraunces', serif" }}>
              {inventory.length}<span style={{ fontSize: "0.8rem", fontWeight: 400, color: S.muted }}> / {protocol.length}</span>
            </div>
          </div>
          <div style={{ background: lowCount > 0 ? S.accent2Light : S.white, border: `1px solid ${lowCount > 0 ? S.accent2 + "44" : S.border}`, borderRadius: 14, padding: "14px 20px", flex: 1, minWidth: 140 }}>
            <div style={labelStyle}>Low Stock</div>
            <div style={{ fontSize: "1.3rem", fontWeight: 800, color: lowCount > 0 ? S.accent2 : S.ink, fontFamily: "'Fraunces', serif" }}>
              {lowCount}
            </div>
          </div>
          <div style={{ background: S.white, border: `1px solid ${S.border}`, borderRadius: 14, padding: "14px 20px", flex: 1, minWidth: 140 }}>
            <div style={labelStyle}>Total Doses Left</div>
            <div style={{ fontSize: "1.3rem", fontWeight: 800, color: S.accent, fontFamily: "'Fraunces', serif" }}>
              {items.reduce((sum, i) => sum + i.remaining, 0)}
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {protocol.length === 0 && (
        <Card style={{ textAlign: "center", padding: 48 }}>
          <div style={{ fontSize: "2.4rem", marginBottom: 12 }}>📦</div>
          <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: "1.1rem", color: S.ink, marginBottom: 8 }}>
            No peptides in your protocol yet
          </div>
          <p style={{ color: S.muted, fontSize: "0.82rem", lineHeight: 1.6 }}>
            Add peptides to your protocol first, then come back here to track your supply.
          </p>
        </Card>
      )}

      {/* Peptide inventory cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {items.map(item => {
          const isEditing = adding === item.peptide_id;
          const statusColor = item.isEmpty ? "#dc2626" : item.isLow ? S.accent2 : S.accent;
          const statusLabel = item.isEmpty ? "Out of stock" : item.isLow ? "Low stock" : item.inv ? "In stock" : "Not tracked";
          const barColor = item.isEmpty ? "#dc2626" : item.isLow ? S.accent2 : S.accent;

          return (
            <Card key={item.peptide_id} style={{ padding: 0, overflow: "hidden" }}>
              {/* Card header */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "18px 22px", borderBottom: isEditing ? `1px solid ${S.border}` : "none" }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: item.color || S.accent, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.92rem", color: S.ink }}>{item.name}</div>
                  <div style={{ fontSize: "0.72rem", color: S.muted, marginTop: 2 }}>
                    {item.dose || item.dosing} · {item.route}
                  </div>
                </div>

                {/* Status badge */}
                {item.inv && (
                  <span style={{
                    fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.04em",
                    padding: "4px 10px", borderRadius: 100,
                    background: item.isEmpty ? "#dc262622" : item.isLow ? S.accent2Light : S.accentLight,
                    color: statusColor,
                    border: `1px solid ${statusColor}33`
                  }}>
                    {statusLabel}
                  </span>
                )}

                {item.inv ? (
                  <button onClick={() => openEdit(item)} style={{ background: "none", border: "none", color: S.muted, cursor: "pointer", fontSize: "0.78rem", fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>
                    Edit
                  </button>
                ) : (
                  <button onClick={() => openAdd(item)} style={{ background: "none", border: "none", color: S.accent, cursor: "pointer", fontSize: "0.78rem", fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>
                    + Track
                  </button>
                )}
              </div>

              {/* Inventory details (if tracked and not editing) */}
              {item.inv && !isEditing && (
                <div style={{ padding: "14px 22px 18px" }}>
                  <div style={{ display: "flex", gap: 24, marginBottom: 12, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ ...labelStyle, marginBottom: 2 }}>Vials</div>
                      <div style={{ fontSize: "1rem", fontWeight: 700, color: S.ink }}>{item.vialsOnHand}</div>
                    </div>
                    <div>
                      <div style={{ ...labelStyle, marginBottom: 2 }}>Doses/Vial</div>
                      <div style={{ fontSize: "1rem", fontWeight: 700, color: S.ink }}>{item.dpv}</div>
                    </div>
                    <div>
                      <div style={{ ...labelStyle, marginBottom: 2 }}>Used</div>
                      <div style={{ fontSize: "1rem", fontWeight: 700, color: S.ink }}>{item.dosesUsed}</div>
                    </div>
                    <div>
                      <div style={{ ...labelStyle, marginBottom: 2 }}>Remaining</div>
                      <div style={{ fontSize: "1rem", fontWeight: 700, color: statusColor }}>{item.remaining}</div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div style={{ background: S.bg, borderRadius: 6, height: 6, overflow: "hidden" }}>
                    <div style={{ width: `${item.pct}%`, height: "100%", background: barColor, borderRadius: 6, transition: "width 0.3s" }} />
                  </div>

                  {/* Restock button */}
                  {item.dosesUsed > 0 && (
                    <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                      <button onClick={() => handleRestock(item)} style={{
                        background: "none", border: `1px solid ${S.border}`, borderRadius: 100,
                        padding: "6px 14px", fontSize: "0.72rem", fontWeight: 600, color: S.ink2,
                        cursor: "pointer", fontFamily: "'Inter', sans-serif"
                      }}>
                        Reset dose count
                      </button>
                    </div>
                  )}

                  {item.inv?.notes && (
                    <p style={{ fontSize: "0.75rem", color: S.muted, marginTop: 10, fontStyle: "italic" }}>{item.inv.notes}</p>
                  )}
                </div>
              )}

              {/* Edit/Add form */}
              {isEditing && (
                <div style={{ padding: "18px 22px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <Input label="Vials on hand" type="number" value={vials} onChange={e => setVials(e.target.value)} placeholder="e.g. 3" />
                    <Input label="Doses per vial" type="number" value={dosesPerVial} onChange={e => setDosesPerVial(e.target.value)} placeholder="e.g. 30" />
                  </div>
                  <Input label="Reorder when doses drop below" type="number" value={threshold} onChange={e => setThreshold(e.target.value)} placeholder="e.g. 5" />
                  <Input label="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Supplier, lot number..." />
                  <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                    <BtnPrimary onClick={handleSave} style={{ padding: "10px 24px", fontSize: "0.82rem" }}>Save</BtnPrimary>
                    <BtnGhost onClick={() => setAdding(null)} style={{ padding: "10px 20px", fontSize: "0.82rem" }}>Cancel</BtnGhost>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export { Inventory };
